import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, Trophy, Flame, CheckCircle, XCircle, Sparkles, Send, RefreshCw, Award } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DailyQuiz() {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [streak, setStreak] = useState({ streak: 0, attempted_today: false });
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    document.title = 'Daily Current Affairs Quiz — Free 10 MCQs | VIDYA365';
    const token = localStorage.getItem('studentToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    Promise.all([
      fetch(`${API}/api/quiz/today`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/api/quiz/streak`, { headers }).then((r) => r.json()).catch(() => ({ streak: 0 })),
      fetch(`${API}/api/quiz/leaderboard`).then((r) => r.json()).catch(() => []),
    ]).then(([q, s, l]) => {
      setQuiz(q);
      setStreak(s);
      setLeaderboard(l || []);
      setLoading(false);
    });
  }, []);

  const submit = async () => {
    if (Object.keys(answers).length < (quiz?.questions?.length || 0)) {
      if (!window.confirm('You haven\'t answered all questions. Submit anyway?')) return;
    }
    setSubmitting(true);
    const token = localStorage.getItem('studentToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API}/api/quiz/submit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          quiz_date: quiz.quiz_date,
          answers: quiz.questions.map((_, i) => ({ q_index: i, answer: answers[i] || 'x' })),
        }),
      });
      const data = await res.json();
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { alert('Submission failed. Try again.'); }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-gray-50"><Header /><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-red-600" size={40} /></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-br from-purple-700 via-indigo-700 to-blue-700 text-white py-10">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Badge className="bg-yellow-400 text-gray-900 mb-2 animate-pulse">DAILY · FREE · 10 QUESTIONS</Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" data-testid="quiz-h1">Daily Current Affairs Quiz</h1>
          <p className="text-indigo-100 mb-4">Fresh 10 MCQs every morning. Build your streak. Climb the leaderboard.</p>
          {streak.streak > 0 && (
            <div className="inline-flex items-center gap-2 bg-orange-500/30 backdrop-blur px-4 py-2 rounded-full text-sm">
              <Flame className="text-yellow-300" size={18} /> Your Streak: <strong>{streak.streak} days</strong>
            </div>
          )}
        </div>
      </section>

      <section className="container mx-auto px-4 py-8 max-w-4xl">
        {!quiz ? (
          <Card><CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-2">Today's quiz is not ready yet.</p>
            <p className="text-sm text-gray-500">Check back later — we publish daily at 7 AM IST.</p>
          </CardContent></Card>
        ) : result ? (
          <ResultView result={result} quiz={quiz} onRetry={() => window.location.reload()} />
        ) : (
          <QuizForm quiz={quiz} answers={answers} setAnswers={setAnswers} onSubmit={submit} submitting={submitting} />
        )}

        {leaderboard.length > 0 && (
          <Card className="mt-6" data-testid="leaderboard-card">
            <CardContent className="p-5">
              <h3 className="font-bold mb-3 flex items-center"><Trophy className="mr-2 text-yellow-600" size={18} /> This Week's Top Scorers</h3>
              <div className="space-y-1 text-sm">
                {leaderboard.slice(0, 10).map((r) => (
                  <div key={r.rank} className="flex justify-between p-2 bg-gray-50 rounded" data-testid={`lb-row-${r.rank}`}>
                    <span><Badge className="mr-2 bg-indigo-100 text-indigo-700">#{r.rank}</Badge>{r.name}</span>
                    <span className="text-gray-600">{r.score} pts · {r.attempts} quizzes · 🔥 {r.streak}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <Footer />
    </div>
  );
}

const QuizForm = ({ quiz, answers, setAnswers, onSubmit, submitting }) => (
  <div className="space-y-4" data-testid="quiz-form">
    {quiz.questions.map((q, i) => (
      <Card key={i}><CardContent className="p-5">
        <p className="font-medium mb-3"><span className="text-red-600 mr-1">Q{i + 1}.</span> {q.q}</p>
        <div className="space-y-2">
          {['a', 'b', 'c', 'd'].map((k) => (
            <label key={k} className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${answers[i] === k ? 'bg-red-50 border-red-500' : 'border-gray-200 hover:bg-gray-50'}`}>
              <input type="radio" name={`q-${i}`} checked={answers[i] === k} onChange={() => setAnswers({ ...answers, [i]: k })} className="mt-1" data-testid={`q${i}-${k}`} />
              <span className="text-sm"><strong className="mr-1">{k.toUpperCase()}.</strong> {q.options[k]}</span>
            </label>
          ))}
        </div>
      </CardContent></Card>
    ))}
    <div className="text-center">
      <Button onClick={onSubmit} disabled={submitting} className="bg-red-600 hover:bg-red-700 px-8 py-5" data-testid="submit-quiz-btn">
        {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
        Submit Quiz ({Object.keys(answers).length}/{quiz.questions.length} answered)
      </Button>
    </div>
  </div>
);

const ResultView = ({ result, quiz, onRetry }) => (
  <div>
    <Card className="border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 to-purple-50 mb-4" data-testid="quiz-result">
      <CardContent className="p-6 text-center">
        <Award className="mx-auto mb-2 text-yellow-600" size={40} />
        <h2 className="text-2xl font-bold">Score: {result.score} / {result.total}</h2>
        <p className="text-gray-600 mb-2">{result.percentage}% • {result.percentage >= 70 ? 'Excellent!' : result.percentage >= 40 ? 'Good effort' : 'Keep practising!'}</p>
        {result.streak > 0 && <Badge className="bg-orange-500 text-white"><Flame size={14} className="mr-1" /> Streak: {result.streak} days</Badge>}
      </CardContent>
    </Card>
    <div className="space-y-3">
      {result.review.map((r) => (
        <Card key={r.q_index}><CardContent className="p-4">
          <div className="flex items-start gap-2 mb-2">
            {r.is_right ? <CheckCircle className="text-green-600 mt-1" size={18} /> : <XCircle className="text-red-600 mt-1" size={18} />}
            <p className="font-medium text-sm flex-1">Q{r.q_index + 1}. {quiz.questions[r.q_index].q}</p>
          </div>
          <p className="text-xs ml-6"><span className="text-gray-500">Your answer: </span><strong>{r.your_answer.toUpperCase()}</strong> {!r.is_right && <span className="text-green-700"> · Correct: {r.correct_answer.toUpperCase()}</span>}</p>
          <p className="text-xs text-gray-600 ml-6 mt-1 italic">{r.explanation}</p>
        </CardContent></Card>
      ))}
    </div>
    <div className="text-center mt-6">
      <Button onClick={onRetry} variant="outline"><RefreshCw className="mr-2" size={14} /> View Again</Button>
      <Link to="/ai-interview" className="ml-2"><Button className="bg-indigo-600 hover:bg-indigo-700"><Sparkles className="mr-2" size={14} /> Try AI Mock Interview</Button></Link>
    </div>
  </div>
);
